/**
 * RCC Surface #15 — CTR during disaster declarations.
 *
 * FinCEN offers relaxed CTR aggregation rules during disaster declarations.
 * This surface is an advisory checklist + shows the current disaster status
 * (sourced from config.systemSettings['DisasterDeclarationActive']).
 */

import { useConfig } from '@/contexts/ConfigContext';
import { Pill } from '@/components/primitives';
import { SurfaceHeader } from '../components/SurfaceHeader';

export function CtrDisaster(): JSX.Element {
  const { config } = useConfig();
  const active = Boolean(config.systemSettings?.['DisasterDeclarationActive']);
  const declaration = (config.systemSettings?.['DisasterDeclarationRef'] as string | undefined) ?? null;

  return (
    <div>
      <SurfaceHeader
        title="CTR during Disaster Declarations"
        scope="FinCEN offers relaxed CTR aggregation during declared disasters. This surface tracks the active declaration flag from system settings."
        surfaceNumber={15}
        role="FSR · Compliance oversight"
        actions={
          active ? (
            <Pill variant="err">Declaration ACTIVE</Pill>
          ) : (
            <Pill variant="muted">No active declaration</Pill>
          )
        }
      />

      <section
        style={{
          padding: 'var(--sp-7)',
          background: active ? 'var(--err-bg)' : 'var(--ink-50)',
          color: active ? 'var(--err-text)' : 'var(--ink-800)',
          border: `1px solid ${active ? 'var(--pcb-red)' : 'var(--surface-border)'}`,
          borderRadius: 'var(--r-md)',
          marginBottom: 'var(--sp-6)',
        }}
      >
        {active ? (
          <div>
            <strong>Disaster declaration in effect.</strong> Reference: {declaration ?? '—'}. Consult
            BSA Officer before adjusting CTR aggregation rules; document exceptions in SAR narrative.
          </div>
        ) : (
          <div>
            No disaster declaration active. Standard CTR rules apply. Flag is toggled by SuperAdmin via
            <code> config.systemSettings.DisasterDeclarationActive</code>.
          </div>
        )}
      </section>

      <section
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--surface-border)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-7)',
        }}
      >
        <h3 style={{ marginTop: 0 }}>Disaster-mode checklist</h3>
        <ul style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--ink-700)' }}>
          <li>Confirm declaration reference (FEMA or state) in SystemSettings.</li>
          <li>Document every CTR exception in the SAR narrative (5-yr retention).</li>
          <li>Coordinate with BSA Officer before clearing any aggregation.</li>
          <li>Review FinCEN advisory for the specific event before application.</li>
        </ul>
      </section>
    </div>
  );
}
